<template>
	<div>
		<div class="row content-header">
			<div class="content-header-left mb-2 col-md-9 col-12">
				<div class="row breadcrumbs-top">
					<div class="col-12">
						<h2 class="content-header-title float-left pr-1 mb-0">
							<feather-icon icon="ArrowLeftIcon" size="24" class="mr-2" @click="goBack" />Edit Medicine Purchase
						</h2>
						<div class="breadcrumb-wrapper">
							<ol class="breadcrumb">
								<li class="breadcrumb-item active">
									<span aria-current="location">Edit</span>
								</li>
							</ol>
						</div>
					</div>
				</div>
			</div>
		</div>

		<div v-if="loading" class="text-center py-5">
			<b-spinner variant="primary" label="Loading…" />
		</div>

		<validation-observer v-else ref="simpleRules">
			<b-form @submit.prevent="updateMedicine">
				<b-card>
					<b-row>
						<b-col md="6">
							<b-form-group>
								<template v-slot:label>Purchase Date<span class="text-danger">*</span></template>
								<validation-provider #default="{ errors }" name="purchase_date" rules="required">
									<b-form-datepicker
										v-model="FormData.purchase_date"
										menu-class="w-100"
										calendar-width="100%"
										class="mb-1"
									/>
									<small class="text-danger">{{ errors[0] }}</small>
								</validation-provider>
							</b-form-group>
						</b-col>

						<b-col md="6">
							<b-form-group>
								<template v-slot:label>Production Type<span class="text-danger">*</span></template>
								<validation-provider #default="{ errors }" name="Production Type" rules="required">
									<v-select
										v-model="FormData.production_type_id"
										:options="productionTypes"
										label="name"
										:reduce="(e) => e.id"
									/>
									<small class="text-danger">{{ errors[0] }}</small>
								</validation-provider>
							</b-form-group>
						</b-col>

						<b-col md="6">
							<b-form-group>
								<template v-slot:label>Source<span class="text-danger">*</span></template>
								<validation-provider #default="{ errors }" name="Source" rules="required">
									<v-select
										v-model="FormData.source_id"
										:options="sourceList"
										label="name"
										:reduce="(e) => e.id"
									/>
									<small class="text-danger">{{ errors[0] }}</small>
								</validation-provider>
							</b-form-group>
						</b-col>

						<b-col md="6">
							<b-form-group>
								<template v-slot:label>Medicine Type<span class="text-danger">*</span></template>
								<validation-provider #default="{ errors }" name="Medicine Type" rules="required">
									<v-select
										v-model="FormData.medicine_id"
										:options="medicineList"
										label="name"
										:reduce="(e) => e.id"
										@input="onMedicineSelect"
									/>
									<small class="text-danger">{{ errors[0] }}</small>
								</validation-provider>
							</b-form-group>
						</b-col>

						<b-col md="6">
							<b-form-group>
								<template v-slot:label>Unit</template>
								<b-form-input v-model="unit" type="text" disabled />
							</b-form-group>
						</b-col>

						<b-col md="5">
							<b-form-group>
								<template v-slot:label>Quantity<span class="text-danger">*</span></template>
								<validation-provider
									#default="{ errors }"
									name="Quantity"
									:rules="{ required: true, regex: RegExp(/^\d+(\.\d{1,4})?$/) }"
								>
									<b-form-input
										v-model="FormData.quantity"
										placeholder="Quantity"
										type="text"
										maxlength="10"
									/>
									<small class="text-danger">{{ errors[0] }}</small>
								</validation-provider>
							</b-form-group>
						</b-col>

						<b-col md="1" style="align-self: center">
							<div v-if="unit === 'ml'">Liter</div>
							<div v-else-if="unit === 'g' || unit === 'mg' || unit === 'kg'">Kg</div>
						</b-col>

						<b-col md="6">
							<b-form-group>
								<template v-slot:label>Expiry Date<span class="text-danger">*</span></template>
								<validation-provider #default="{ errors }" name="Expiry date" rules="required">
									<b-form-datepicker
										v-model="FormData.expiry_date"
										menu-class="w-100"
										calendar-width="100%"
										class="mb-1"
									/>
									<small class="text-danger">{{ errors[0] }}</small>
								</validation-provider>
							</b-form-group>
						</b-col>

						<b-col md="6">
							<b-form-group>
								<template v-slot:label>Total Cost (Rs.)<span class="text-danger">*</span></template>
								<validation-provider
									#default="{ errors }"
									name="Total Cost"
									:rules="{ required: true, regex: RegExp(/^\d+(\.\d{0,2})?$/) }"
								>
									<b-form-input
										v-model="FormData.total_cost"
										placeholder="Total Cost (Rs.)"
										type="text"
										maxlength="10"
									/>
									<small class="text-danger">{{ errors[0] }}</small>
								</validation-provider>
							</b-form-group>
						</b-col>

						<b-col md="6">
							<b-form-group>
								<template v-slot:label>Batch No. <span class="text-danger">*</span></template>
								<b-form-input v-model="FormData.batch_no" placeholder="Batch No." type="text" disabled />
								<small class="text-muted">Batch number cannot be changed after creation.</small>
							</b-form-group>
						</b-col>

						<b-col md="12">
							<b-button type="submit" variant="primary" class="mr-1 mb-3" :disabled="submitting">
								<b-spinner v-if="submitting" small class="mr-1" />
								Update
							</b-button>
							<b-button variant="outline-secondary" class="mb-3" @click="goBack">Cancel</b-button>
						</b-col>
					</b-row>
				</b-card>
			</b-form>
		</validation-observer>
	</div>
</template>

<script>
import axiosIns from "@/libs/axios";
import vSelect from "vue-select";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required, regex } from "@validations";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
	components: {
		vSelect,
		ValidationProvider,
		ValidationObserver,
	},

	data() {
		return {
			required,
			regex,
			loading: true,
			submitting: false,
			FormData: {
				purchase_date: null,
				production_type_id: null,
				source_id: null,
				medicine_id: null,
				expiry_date: null,
				quantity: null,
				total_cost: null,
				batch_no: null,
			},
			productionTypes: [],
			medicineList: [],
			sourceList: [],
			unit: null,
		};
	},

	created() {
		Promise.all([
			this.getSource(),
			this.getMedicines(),
			this.getProductionTypes(),
		])
			.catch(() => {})
			.finally(() => {
				this.loadPurchase();
			});
	},

	methods: {
		goBack() {
			this.$router.go(-1);
		},

		getMedicines() {
			return axiosIns.get("web/get-all-medicines").then((response) => {
				this.medicineList = response.data.medicine_list;
			});
		},

		getSource() {
			return axiosIns.get("web/get-all-sources").then((response) => {
				this.sourceList = response.data.source_list;
			});
		},

		getProductionTypes() {
			return axiosIns.get("web/production-types").then((response) => {
				this.productionTypes = response.data.production_type.data;
			});
		},

		loadPurchase() {
			const farmId = this.$route.params.farmId;
			const purchaseId = this.$route.params.purchaseId;
			axiosIns
				.get(`web/farm/${farmId}/purchase-medicine/${purchaseId}`)
				.then((response) => {
					const p = response.data.purchase;
					this.FormData.purchase_date = p.purchase_date;
					this.FormData.production_type_id = p.production_type_id;
					this.FormData.source_id = p.source_id;
					this.FormData.medicine_id = p.medicine_id;
					this.FormData.expiry_date = p.expiry_date;
					this.FormData.quantity = p.quantity;
					this.FormData.total_cost = p.total_cost;
					this.FormData.batch_no = p.batch_no;
					// Set unit from loaded medicine
					const med = this.medicineList.find((m) => m.id === p.medicine_id);
					if (med && med.unit) {
						this.unit = med.unit.name;
					}
					this.loading = false;
				})
				.catch(() => {
					this.$toast({
						component: ToastificationContent,
						props: { icon: "AlertCircleIcon", variant: "danger", text: "Failed to load purchase record." },
					});
					this.$router.go(-1);
				});
		},

		onMedicineSelect(val) {
			if (val && typeof val === "object" && val.unit) {
				this.unit = val.unit.name;
			}
		},

		updateMedicine() {
			this.$refs.simpleRules.validate().then((success) => {
				if (!success) return;
				this.submitting = true;
				const farmId = this.$route.params.farmId;
				const purchaseId = this.$route.params.purchaseId;
				axiosIns
					.put(`web/farm/${farmId}/purchase-medicine/${purchaseId}`, {
						production_type_id: this.FormData.production_type_id,
						source_id: this.FormData.source_id,
						medicine_id: this.FormData.medicine_id,
						purchase_date: this.FormData.purchase_date,
						expiry_date: this.FormData.expiry_date,
						quantity: this.FormData.quantity,
						total_cost: this.FormData.total_cost,
					})
					.then(() => {
						this.$toast({
							component: ToastificationContent,
							props: { icon: "CheckCircleIcon", variant: "success", text: "Medicine purchase updated successfully." },
						});
						this.$router.go(-1);
					})
					.catch((error) => {
						const msg =
							error.response?.data?.message ||
							Object.values(error.response?.data?.errors || {}).flat().join(", ") ||
							"Update failed.";
						this.$toast({
							component: ToastificationContent,
							props: { icon: "AlertCircleIcon", variant: "danger", text: msg },
						});
					})
					.finally(() => {
						this.submitting = false;
					});
			});
		},
	},
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
